import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import type { ApiKeyEnvironment, IssueKeyRequest, IssuedKey, OperatingMode } from '@/api/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { OneTimeSecretContent } from '@/components/OneTimeSecretContent';
import { PermissionChecklist } from '@/components/PermissionChecklist';
import { Button } from '@/components/ui/button';
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { permissionsFieldError } from '@/lib/keyPermissions';

const issueKeySchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name is too long.'),
  environment: z.enum(['LIVE', 'TEST']),
  permissions: z.array(z.string()).min(1, 'Select at least one permission.'),
});
type IssueKeyForm = z.infer<typeof issueKeySchema>;

export function IssueKeyDialog({
  issueKey,
  invalidateKey,
  availablePermissions,
  operatingMode,
  onDone,
}: {
  issueKey: (request: IssueKeyRequest) => Promise<IssuedKey>;
  invalidateKey: readonly unknown[];
  availablePermissions: readonly string[];
  /** Set for a partner key; leaves the checklist ungrouped when absent (org keys). */
  operatingMode?: OperatingMode | undefined;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IssueKeyForm>({
    resolver: zodResolver(issueKeySchema),
    defaultValues: { name: '', environment: 'LIVE' as ApiKeyEnvironment, permissions: [] },
  });
  const permissions = watch('permissions');

  const issue = useMutation({
    mutationFn: (values: IssueKeyForm) => issueKey(values),
    onSuccess: (issued) => {
      setSecret(issued.one_time_secret);
      void queryClient.invalidateQueries({ queryKey: invalidateKey });
    },
  });

  const permissionsError = permissionsFieldError(issue.error);

  if (secret) {
    return (
      <OneTimeSecretContent
        title="Key issued"
        warning="This secret is shown once and cannot be retrieved again. Copy it now — if it is lost, revoke this key and issue a new one."
        secret={secret}
        onDone={() => {
          setSecret(null);
          onDone();
        }}
      />
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Issue an API key</DialogTitle>
      </DialogHeader>

      {issue.isError && !permissionsError && (
        <Alert variant="destructive">
          <AlertDescription>
            {isApiError(issue.error) ? issue.error.message : 'Could not issue the key.'}
          </AlertDescription>
        </Alert>
      )}

      <form id="issue-key" onSubmit={handleSubmit((values) => issue.mutate(values))}>
        <FieldGroup>
          <Field data-invalid={Boolean(errors.name)}>
            <FieldLabel htmlFor="key-name">Name</FieldLabel>
            <Input id="key-name" autoFocus {...register('name')} />
            <FieldError errors={[errors.name]} />
          </Field>
          <Field>
            <FieldLabel htmlFor="key-environment">Environment</FieldLabel>
            <Select
              defaultValue="LIVE"
              onValueChange={(value) => setValue('environment', value as ApiKeyEnvironment)}
            >
              <SelectTrigger id="key-environment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="LIVE">Live</SelectItem>
                <SelectItem value="TEST">Test</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field data-invalid={Boolean(errors.permissions ?? permissionsError)}>
            <PermissionChecklist
              value={permissions}
              onChange={(next) => setValue('permissions', next, { shouldValidate: true })}
              available={availablePermissions}
              operatingMode={operatingMode}
              error={permissionsError ?? errors.permissions?.message}
            />
          </Field>
        </FieldGroup>
      </form>

      <DialogFooter>
        <Button type="submit" form="issue-key" disabled={issue.isPending}>
          {issue.isPending ? 'Issuing…' : 'Issue key'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
